import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * CTX-167 "breadcrumb navigation" — Account -> Region -> VPC -> AZ -> Subnet
 * trail over `InsightTopologyCanvas`'s hierarchy-disclosure focus state. Pure
 * presentation: every segment/back action is a callback into the canvas,
 * which owns the actual `focusedVpcId`/`focusedAzId`/`expandedSubnetIds`
 * state — this component never reads or writes it directly.
 *
 * Clicking a segment jumps straight to that level (clearing everything more
 * specific than it); the dedicated Back button and the canvas's own Escape
 * handler both call `onBack` instead, which only clears the single
 * most-specific level currently active (subnet, then AZ, then VPC), per the
 * ticket's "Back and Escape return one level while preserving the source
 * selection."
 */

export interface TopologyBreadcrumbSegment {
	id: string;
	label: string;
}

export interface TopologyBreadcrumbsProps {
	account: string | null;
	region: string | null;
	vpc: TopologyBreadcrumbSegment | null;
	az: TopologyBreadcrumbSegment | null;
	subnet: TopologyBreadcrumbSegment | null;
	/** Whether a "Back one level" action is currently available (VPC, AZ, or subnet focus is active). */
	canGoBack: boolean;
	onBack: () => void;
	onNavigateToRoot: () => void;
	onNavigateToVpc: () => void;
	onNavigateToAz: () => void;
}

function Segment({
	label,
	isCurrent,
	onClick,
	testId,
}: {
	label: string;
	isCurrent: boolean;
	onClick?: () => void;
	testId: string;
}) {
	if (!onClick || isCurrent) {
		return (
			<span
				data-testid={testId}
				aria-current={isCurrent ? "location" : undefined}
				className={`truncate ${isCurrent ? "font-semibold text-semantic-text-primary" : "text-semantic-text-secondary"}`}
			>
				{label}
			</span>
		);
	}
	return (
		<button
			type="button"
			data-testid={testId}
			onClick={onClick}
			className="truncate text-semantic-text-secondary hover:text-semantic-text-brand hover:underline"
		>
			{label}
		</button>
	);
}

export const TopologyBreadcrumbs: React.FC<TopologyBreadcrumbsProps> = ({
	account,
	region,
	vpc,
	az,
	subnet,
	canGoBack,
	onBack,
	onNavigateToRoot,
	onNavigateToVpc,
	onNavigateToAz,
}) => {
	// The account number already heads the page (source path in the header),
	// so the canvas breadcrumb roots at the region — account only as fallback.
	const rootLabel = region || account || "Region";
	const isRootCurrent = !vpc;

	return (
		<nav
			aria-label="Topology hierarchy"
			data-testid="topology-breadcrumbs"
			className="flex min-w-0 items-center gap-1.5 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary px-2 py-1 text-xs"
		>
			<button
				type="button"
				data-testid="topology-breadcrumb-back"
				aria-label="Back one level"
				disabled={!canGoBack}
				onClick={onBack}
				className="shrink-0 rounded-sm p-0.5 text-semantic-text-secondary hover:bg-semantic-state-active disabled:cursor-not-allowed disabled:opacity-30"
			>
				<ChevronLeft className="h-3.5 w-3.5" />
			</button>
			<Segment label={rootLabel} isCurrent={isRootCurrent} onClick={vpc ? onNavigateToRoot : undefined} testId="topology-breadcrumb-root" />
			{vpc && (
				<>
					<ChevronRight className="h-3 w-3 shrink-0 text-semantic-text-tertiary" aria-hidden="true" />
					<Segment label={vpc.label} isCurrent={!az && !subnet} onClick={az || subnet ? onNavigateToVpc : undefined} testId="topology-breadcrumb-vpc" />
				</>
			)}
			{vpc && az && (
				<>
					<ChevronRight className="h-3 w-3 shrink-0 text-semantic-text-tertiary" aria-hidden="true" />
					<Segment label={az.label} isCurrent={!subnet} onClick={subnet ? onNavigateToAz : undefined} testId="topology-breadcrumb-az" />
				</>
			)}
			{vpc && subnet && (
				<>
					<ChevronRight className="h-3 w-3 shrink-0 text-semantic-text-tertiary" aria-hidden="true" />
					<Segment label={subnet.label} isCurrent testId="topology-breadcrumb-subnet" />
				</>
			)}
		</nav>
	);
};

export default TopologyBreadcrumbs;
